import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert, Copy, Check, LogOut } from "lucide-react";
import RecoveryCodesDialog from "@/components/RecoveryCodesDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mandatory 2FA setup overlay shown when a logged-in user has not yet enabled 2FA.
 */
export default function Mandatory2faSetup({ user, onCompleted, onLogout }) {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
        setSetupData(data);
      } catch (err) {
        setError(err.response?.data?.detail || "Setup konnte nicht gestartet werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const confirm = async () => {
    setError(""); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/enable`, { code }, { withCredentials: true });
      // Show recovery codes; only proceed to dashboard after user confirms saving them
      setRecoveryCodes(data.recovery_codes || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Ungültiger Code");
    } finally {
      setSubmitting(false);
    }
  };

  const finishAfterCodes = async () => {
    setRecoveryCodes(null);
    if (onCompleted) await onCompleted();
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div data-testid="mandatory-2fa-screen" className="min-h-screen bg-[#0C1620] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-[#12202f] border border-amber-400/30 rounded-lg overflow-hidden">
          <div className="bg-amber-400/10 border-b border-amber-400/20 px-6 py-4 flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <h1 className="font-heading text-lg text-white">Zwei-Faktor-Authentifizierung erforderlich</h1>
              <p className="text-xs text-[#8899AA] mt-0.5">Diese ist für alle Admin-Benutzer verpflichtend.</p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : setupData ? (
              <>
                <p className="text-sm text-[#8899AA]">
                  Eingeloggt als <strong className="text-white">{user?.email}</strong>. Bitte richten Sie 2FA jetzt ein, um auf das Admin-Panel zuzugreifen.
                </p>

                <ol className="text-xs text-[#8899AA] space-y-1.5 list-decimal list-inside">
                  <li>Öffnen Sie <strong className="text-white">Microsoft Authenticator</strong> auf Ihrem Smartphone.</li>
                  <li>Tippen Sie auf „+" → „Anderes Konto" und scannen Sie den QR-Code.</li>
                  <li>Geben Sie den 6-stelligen Code unten ein, um die Einrichtung abzuschließen.</li>
                </ol>

                <div className="flex justify-center bg-white p-4 rounded-lg">
                  <QRCodeSVG value={setupData.otpauth_uri} size={200} level="M" />
                </div>

                <div className="bg-[#0C1620] border border-[#3A4F60]/40 rounded p-3">
                  <p className="text-[10px] text-[#3A4F60] uppercase tracking-wider mb-1">Secret manuell (falls QR nicht funktioniert)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-[#C5993A] font-mono break-all flex-1" data-testid="mandatory-2fa-secret">{setupData.secret}</code>
                    <Button onClick={copySecret} variant="ghost" size="sm" className="h-7 text-[#8899AA] hover:text-[#C5993A] flex-shrink-0">
                      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[#8899AA] mb-1.5">6-stelliger Code aus der App</label>
                  <Input
                    data-testid="mandatory-2fa-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-center text-2xl tracking-[0.5em] focus:border-[#C5993A]"
                  />
                </div>

                {error && <p data-testid="mandatory-2fa-error" className="text-red-400 text-sm">{error}</p>}

                <div className="flex flex-col gap-2">
                  <Button
                    data-testid="mandatory-2fa-activate"
                    onClick={confirm}
                    disabled={submitting || code.length !== 6}
                    className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
                  >
                    {submitting ? "Prüfe..." : "2FA aktivieren und fortfahren"}
                  </Button>
                  <Button
                    data-testid="mandatory-2fa-logout"
                    onClick={onLogout}
                    variant="ghost"
                    className="w-full text-[#8899AA] hover:text-[#C5993A] text-sm"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Abmelden
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-red-400 text-sm">{error}</p>
                <Button onClick={onLogout} variant="ghost" className="w-full text-[#8899AA]">Abmelden</Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <RecoveryCodesDialog
        open={!!recoveryCodes}
        codes={recoveryCodes || []}
        onClose={finishAfterCodes}
        title="2FA aktiviert — Recovery-Codes speichern"
      />
    </div>
  );
}
